import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ColGroupDef, themeQuartz } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import {
  listComplianceAuditFolders,
  createComplianceAuditFolder,
  updateComplianceAuditFolder,
  downloadComplianceAuditFolder,
} from "../../../service/api";
import { SpinLoader } from "../../../components/loader/Loader";
import { usePermissions } from "../../../hooks/usePermission";
import { COMPLIANCE_AUDIT_DOCUMENTATION, POLICY_MODULE } from "../../../constants/modules";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

interface folderType {
  createdAt: number;
  updatedAt: number;
  id: string;
  folderName: string;
  files: null | any[];
  createdDate: string;
  uploadedBy: string;
  totalsize?: string;
}

const ComplianceAuditDocumentation = () => {
  const [visible, setVisible] = React.useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [resMessage, setResMessage] = useState<string>("");
  const [createModalMessage, setCreateModalMessage] = useState<string>(""); // Message for Create Folder modal
  const [editModalMessage, setEditModalMessage] = useState<string>(""); // Message for Edit Folder modal
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [folderName, setFolderName] = useState<string>("");
  const [editFolder, setEditFolder] = useState<folderType | null>(null);
  const navigate = useNavigate();
  const [folders, setFolders] = useState<folderType[]>([]);

  const { canCreate, canRead, canUpdate } = usePermissions();
  const onClose = () => {
    setVisible(false);
    setCreateModalMessage(""); // Clear message when closing the modal
    setFolderName(""); // Clear folder name input
  };

  const onEditClose = () => {
    setEditVisible(false);
    setEditFolder(null);
    setFolderName("");
    setEditModalMessage(""); // Clear message when closing the edit modal
  };

  const handleFolderClick = useCallback((folder: folderType) => {
    navigate(`/safety-facility-rounds/compliance-audit-documentation/folder/${folder.id}`);
  }, [navigate]);

  const createFolder = async () => {
    if (!folderName.trim()) {
      setCreateModalMessage("Folder name cannot be empty");
      return;
    }

    // Check for duplicate folder name (case-insensitive)
    const duplicateFolder = folders.find(
      (folder) =>
        folder.folderName.toLowerCase() === folderName.trim().toLowerCase()
    );
    if (duplicateFolder) {
      setCreateModalMessage(
        `" folder name already exists "`
      );
      return;
    }

    // Clear any previous message
    setCreateModalMessage("");
    setVisible(false);
    setIsLoading(true);
    const data = {
      details: [
        {
          folderName,
          createdDate: new Date().toISOString(),
          uploadedBy: "current_user",
        },
      ],
    };
    try {
      const res = await createComplianceAuditFolder(data);
      if (res.data.resCode === 200) {
        setResMessage(res.data.resMessage);
        fetchFolders();
      } else {
        setResMessage(res.data.resMessage || "Failed to create folder");
      }
    } catch (err: any) {
      setResMessage(err.message ?? "Something went wrong");
    } finally {
      setIsLoading(false);
      setFolderName("");
    }
  };

  const formatSize = (sizeInKB: number): string => {
    if (sizeInKB === 0) return "0 MB";
    const sizeInMB = sizeInKB / 1024;
    if (sizeInMB < 1024) {
      return `${sizeInMB.toFixed(2)} MB`;
    }
    const sizeInGB = sizeInMB / 1024;
    return `${sizeInGB.toFixed(2)} GB`;
  };

  const fetchFolders = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await listComplianceAuditFolders({});

      if (res.data.resCode === 200) {
        const folderData = res.data.message.results || res.data.message || [];

        const folderPromises = folderData.map(async (folder: any) => {
          try {
            let filesWithSizes = folder.files || [];
            let totalSize = 0;

            totalSize = filesWithSizes.reduce((total: number, file: any) => {
              const sizeInKB = parseInt(file.size, 10) || 0;
              return total + sizeInKB;
            }, 0);

            const formattedSize = formatSize(totalSize);
            const formattedFolder = {
              id: folder.id,
              folderName: folder.folderName,
              files: filesWithSizes,
              totalsize: formattedSize,
              createdAt: folder.createdAt || Date.now(),
              updatedAt: folder.updatedAt || Date.now(),
              createdDate: folder.createdDate || new Date().toISOString(),
              uploadedBy: folder.uploadedBy || "unknown",
            };
            return formattedFolder;
          } catch (err) {
            console.error(`Failed to process folder ${folder.id}:`, err);
            return null;
          }
        });

        const foldersWithSizes = (await Promise.all(folderPromises)).filter(
          (folder) => folder !== null
        ) as folderType[];
        setFolders(foldersWithSizes);
        // setResMessage(
        //   foldersWithSizes.length === 0 ? "No folders found" : ""
        // );
      } else {
        setFolders([]);
        setResMessage(res.data.resMessage || "Failed to fetch folders");
      }
    } catch (err: any) {
      console.error("Fetch error:", err);
      setFolders([]);
      setResMessage(err?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleEditFolder = async () => {
    if (!editFolder || !folderName.trim()) {
      setEditModalMessage("Folder name cannot be empty");
      return;
    }

    // Check for duplicate folder name during edit (excluding the folder being edited)
    const duplicateFolder = folders.find(
      (folder) =>
        folder.id !== editFolder.id &&
        folder.folderName.toLowerCase() === folderName.trim().toLowerCase()
    );
    if (duplicateFolder) {
      setEditModalMessage(
        `" folder name already exists "`
      );
      return;
    }

    // Clear any previous message
    setEditModalMessage("");
    setEditVisible(false);
    setIsLoading(true);
    const data = { folderName };
    try {
      const res = await updateComplianceAuditFolder(data, editFolder.id);
      if (res.data.resCode === 200) {
        setResMessage(res.data.resMessage || "Folder updated successfully");
        setFolders(
          folders.map((folder) =>
            folder.id === editFolder.id ? { ...folder, folderName } : folder
          )
        );
      } else {
        // Don't show database errors to user, just log them
        console.error("Folder update failed:", res.data.resMessage);
        setResMessage(""); // Clear any existing error message
      }
    } catch (err: any) {
      // Don't show server errors to user, just log them
      console.error("Folder update error:", err.message);
      setResMessage(""); // Clear any existing error message
    } finally {
      setIsLoading(false);
      setEditFolder(null);
      setFolderName("");
    }
  };

  const handleDownloadFolder = useCallback(async (folderId: string, folderName: string) => {
    try {
      setIsLoading(true);
      const response = await downloadComplianceAuditFolder(folderId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${folderName}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setResMessage(err?.message ?? "Failed to download folder");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const openEditModal = useCallback((folder: folderType) => {
    setEditFolder(folder);
    setFolderName(folder.folderName);
    setEditVisible(true);
  }, []);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  // AG Grid Column Definitions
  const columnDefs = useMemo<(ColDef<folderType> | ColGroupDef<folderType>)[]>(
    () => [
      {
        field: "folderName",
        headerName: "Folders",
        sortable: true,
        flex: 2,
        minWidth: 200,
        filter: false,
        cellStyle: { padding: "10px 0 0 5px" },
        cellRenderer: (params: any) => (
          <div className="flex items-center">
            <img
              src={images.folderIcon}
              alt="Folder"
              className="w-5 h-5 mr-2"
            />
            {params.value}
          </div>
        ),
      },
      {
        field: "files",
        headerName: "No of Files",
        sortable: true,
        flex: 1,
        minWidth: 120,
        filter: false,
        cellStyle: { padding: "10px 0 0 5px" },
        valueGetter: (params: any) => params.data?.files?.length || 0,
        cellRenderer: (params: any) => `${params.value} File(s)`,
      },
      {
        field: "totalsize",
        headerName: "Folder Size",
        sortable: true,
        flex: 1,
        minWidth: 120,
        filter: false,
        cellStyle: { padding: "10px 0 0 5px" },
        valueGetter: (params: any) => params.data?.totalsize || "0 MB",
      },
      {
        headerName: "Action",
        sortable: false,
        flex: 1,
        minWidth: 150,
        filter: false,
        cellStyle: { padding: "10px 0 0 5px" },
        cellRenderer: (params: any) => (
          <div className="flex gap-3 pt-1">
            {canRead(COMPLIANCE_AUDIT_DOCUMENTATION) && (
              <img
                src={images.viewIcon}
                alt="View"
                className="w-5 h-5 cursor-pointer"
                onClick={() => handleFolderClick(params.data)}
              />
            )}
            {canUpdate(COMPLIANCE_AUDIT_DOCUMENTATION) && (
              <img
                src={images.editIcon}
                alt="Edit"
                className="w-5 h-5 cursor-pointer"
                onClick={() => openEditModal(params.data)}
              />
            )}
            {canRead(COMPLIANCE_AUDIT_DOCUMENTATION) && (
              <img
                src={images.downloadIcon}
                alt="Download"
                className="w-5 h-5 cursor-pointer"
                onClick={() =>
                  handleDownloadFolder(params.data.id, params.data.folderName)
                }
              />
            )}
          </div>
        ),
      },
    ],
    [canRead, canUpdate, handleFolderClick, openEditModal, handleDownloadFolder]
  );

  const theme = useMemo(() => gridTheme, []);

  const defaultColDef = useMemo(
    () => ({
      wrapText: true,
      headerClass: "font-bold text-md bg-[#e9f5ff] p-2",
    }),
    []
  );

  return (
    <div className="px-2 py-4 mt-14 w-full h-full">
      <div className="bg-white rounded-lg shadow py-6 px-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">Compliance Audit & Documentation</h1>
          {canCreate(COMPLIANCE_AUDIT_DOCUMENTATION) && (
            <button
              className="flex items-center px-4 py-2 bg-teal-500 text-white rounded-xl"
              onClick={() => setVisible(true)}
            >
              <img
                src={images.createFolderIcon}
                alt="Create"
                className="w-5 h-5 mr-2"
              />
              CREATE FOLDER
            </button>
          )}
        </div>
        {/* Display resMessage for non-modal messages (e.g., API errors) */}
        {resMessage && (
          <p className="text-center py-2 text-gray-600">{resMessage}</p>
        )}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <SpinLoader />
          </div>
        ) : folders.length > 0 ? (
          <div
            className="ag-theme-alpine mt-4 w-full"
            style={{ height: "500px", width: "100%" }}
          >
            <AgGridReact
              rowData={folders}
              columnDefs={columnDefs}
              theme={theme}
              headerHeight={65}
              rowHeight={50}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={10}
              paginationPageSizeSelector={[10, 20, 50]}
            />
          </div>
        ) : (
          <p className="text-center py-4">No folders found</p>
        )}
      </div>
      <Modal isOpen={visible} onClose={onClose}>
        <div className="bg-white w-[500px] rounded-xl overflow-hidden">
          <div className="p-6 leading-none">
            <h1 className="text-xl font-bold">Create Folder</h1>
            <hr className="my-2" />
            <div className="flex flex-col gap-4 mt-4">
              {/* Flexbox layout to place heading and message side by side */}
              <div className="flex items-center gap-3">
                <label htmlFor="folderName" className="font-medium">
                  Folder Name
                </label>
                {createModalMessage && (
                  <p
                    className={`text-sm flex-shrink-0 ${createModalMessage.includes("already exists")
                        ? "text-red-500"
                        : "text-gray-600"
                      }`}
                  >
                    {createModalMessage}
                  </p>
                )}
              </div>
              <input
                type="text"
                id="folderName"
                className="border-2 p-2 rounded"
                placeholder="Enter Folder Name"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
              />
            </div>
          </div>
          <div className="flex bg-[#61748E] justify-end py-3 pr-2 gap-4">
            <button
              className="bg-[#0fa9a6] px-3 py-2 rounded-full text-white"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="bg-[#869ab6] px-3 py-2 rounded-full text-white"
              onClick={createFolder}
            >
              Create
            </button>
          </div>
        </div>
      </Modal>
      <Modal isOpen={editVisible} onClose={onEditClose}>
        <div className="bg-white w-[500px] rounded-xl overflow-hidden">
          <div className="p-6 leading-none">
            <h1 className="text-xl font-bold">Edit Folder</h1>
            <hr className="my-2" />
            <div className="flex flex-col gap-4 mt-4">
              {/* Flexbox layout to place heading and message side by side */}
              <div className="flex items-center gap-3">
                <label htmlFor="editFolderName" className="font-medium">
                  Folder Name
                </label>
                {editModalMessage && (
                  <p
                    className={`text-sm flex-shrink-0 ${editModalMessage.includes("already exists")
                        ? "text-red-500"
                        : "text-gray-600"
                      }`}
                  >
                    {editModalMessage}
                  </p>
                )}
              </div>
              <input
                type="text"
                id="editFolderName"
                className="border-2 p-2 rounded"
                placeholder="Enter Folder Name"
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
              />
            </div>
          </div>
          <div className="flex bg-[#61748E] justify-end py-3 pr-2 gap-4">
            <button
              className="bg-[#0fa9a6] px-3 py-2 rounded-full text-white"
              onClick={onEditClose}
            >
              Cancel
            </button>
            <button
              className="bg-[#869ab6] px-3 py-2 rounded-full text-white"
              onClick={handleEditFolder}
            >
              Save
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ComplianceAuditDocumentation;
